"use client";

import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { ActionButtons, ActionButtonsProps } from "../components/actionButtons";
import { cn } from "../lib/utils";
import { Icon, ICON_PRESET_CLASSES, IconComponent } from "./icon";

const alertVariants = cva(
  `relative flex w-full items-start gap-x-2.5 rounded-lg border px-4 py-3 text-sm @container ${ICON_PRESET_CLASSES.sizeForSVG.lg} [&>svg]:shrink-0 [&>svg]:text-current`,
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        // Disable destructive variant
        // destructive:
        // "border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive",
        success: "border-ui-success bg-ui-success-background text-ui-success",
        error: "border-ui-danger bg-ui-danger-background text-ui-danger",
        warning: "border-ui-warning bg-ui-warning-background text-ui-warning",
        info: "border-ui-info bg-ui-info-background text-ui-info",
      },
      overrideClass: {
        true: "ui-alert",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const variantIconMap = {
  default: null,
  destructive: "destructive",
  success: "success",
  error: "error",
  warning: "warning",
  info: "info",
} as const;

interface AlertProps
  extends
    Omit<React.ComponentPropsWithoutRef<"div">, "title">,
    VariantProps<typeof alertVariants>,
    Pick<
      ActionButtonsProps,
      | "cancelButtonProps"
      | "actionButtonProps"
      | "hasCancelButton"
      | "hasActionButton"
    > {
  icon?: IconComponent;
  title?: React.ReactNode;
}

/**
 * shadcn/ui docs: https://ui.shadcn.com/docs/components/alert
 */
const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      className,
      variant,
      overrideClass = true,

      actionButtonProps,
      cancelButtonProps,
      children,
      hasActionButton: showActionButton = false,
      hasCancelButton: showCancelButton = false,
      icon,
      title,
      ...props
    },
    ref,
  ) => {
    // If no icon is provided, skip the variantIconMap and use
    // the variant to determine the icon
    if (!icon) {
      const iconKey = variant ? variantIconMap[variant] : null;
      if (iconKey) icon = <Icon icon={iconKey} />;
    }

    // The default svg translate is tuned for `title`. If `title`
    // is missing we should align the icon to the top
    // to avoid an extra gap under the alert padding.
    const iconTranslateClass = title
      ? "[&>svg]:translate-y-0.5"
      : "[&>svg]:translate-y-0";

    return (
      <div
        className={cn(
          alertVariants({ variant, overrideClass, className }),
          iconTranslateClass,
        )}
        ref={ref}
        role="alert"
        {...props}
      >
        {icon}

        <div className="flex flex-1 flex-col flex-wrap gap-x-2 gap-y-2 @md:flex-row @md:flex-nowrap">
          <div className="grow space-y-1">
            {title && <AlertTitle>{title}</AlertTitle>}
            {children && <AlertDescription>{children}</AlertDescription>}
          </div>

          <ActionButtons
            actionButtonProps={{
              size: "xs",
              ...actionButtonProps,
            }}
            cancelButtonProps={{
              variant: "outline",
              size: "xs",
              ...cancelButtonProps,
            }}
            className="items-center @md:flex-col-reverse @md:justify-center"
            hasActionButton={showActionButton}
            hasCancelButton={showCancelButton}
          />
        </div>
      </div>
    );
  },
);
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    className={cn(
      "col-start-2 min-h-4 text-base font-medium leading-tight tracking-tight text-foreground @md:line-clamp-1",
      className,
    )}
    ref={ref}
    {...props}
  />
));
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    className={cn(
      "justify-items-start space-y-1 text-sm text-muted-foreground [&_p]:leading-relaxed",
      className,
    )}
    ref={ref}
    {...props}
  />
));
AlertDescription.displayName = "AlertDescription";

export { Alert, alertVariants, type AlertProps };
